import { useState } from 'react';
import { useQuery, type UseMutationResult } from '@tanstack/react-query';

import {
  getPresetVersion,
  listPresets,
  secretPrefixOf,
  type TAuthPreset,
  type TAuthPresetSpec,
} from '../../entities/auth-preset';
import { sourceSecrets, sourceAuthMethods, type TSourceAuthMethod } from '../../entities/source';
import {
  useBindSender,
  useRemoveCheck,
  useRequireAddresses,
  type IStagedSender,
} from './use-bind-sender';

export interface ISourceScheme {
  methods: TSourceAuthMethod[];
  method: TSourceAuthMethod | null;
  methodId: string | null;
  hmacMethodId: string | null;
  checked: boolean;
  preset: TAuthPreset | null;
  senderName: string | null;
  secretPrefix: string | null;
  spec: TAuthPresetSpec | null;
  rangeKeys: string[];
  filteringKeys: string[];
  enabledMethods: TSourceAuthMethod[];
  requireAddresses: UseMutationResult<void, Error, string>;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  hasSecret: boolean;
  addressesOnly: boolean;
  staged: IStagedSender | null;
  stage: (next: IStagedSender | null) => void;
  picking: boolean;
  setPicking: (next: boolean) => void;
  save: UseMutationResult<TSourceAuthMethod, Error, IStagedSender>;
  remove: UseMutationResult<void, Error, string>;
}

export const useSourceScheme = (sourceId: string): ISourceScheme => {
  const [staged, setStaged] = useState<IStagedSender | null>(null);
  // Here, not in the block: other parts of the page ("Add checking first") open the picker too.
  const [picking, setPicking] = useState(false);

  const methods = useQuery({
    queryKey: ['source-auth-methods', sourceId],
    queryFn: () => sourceAuthMethods(sourceId),
  });

  const rows = methods.data ?? [];
  const method = rows.find((row) => row.presetKey) ?? rows[0] ?? null;
  const presetKey = method?.presetKey ?? null;

  const presets = useQuery({
    queryKey: ['auth-presets'],
    queryFn: listPresets,
    enabled: presetKey !== null,
  });

  const preset = presets.data?.data.find((row) => row.key === presetKey) ?? null;
  const version = method?.presetVersion ?? null;

  const pinned = useQuery({
    queryKey: ['auth-preset-version', preset?.id, version],
    queryFn: () => getPresetVersion(preset?.id ?? '', version ?? 0),
    enabled: preset !== null && version !== null,
  });

  const spec = pinned.data?.spec ?? null;
  const ipFilter = rows.find((row) => row.method === 'ip_allowlist' && row.enabled) ?? null;
  // Only a list an enabled ip_allowlist method points at filters anything; a preset's is just published.
  const filteringKeys =
    ipFilter?.useProviderRanges && ipFilter.providerRangesKey ? [ipFilter.providerRangesKey] : [];
  const rangeKeys = [...filteringKeys, ...(spec?.ipRanges ? [spec.ipRanges.key] : [])];

  const credentialed = rows.find((row) => row.method !== 'ip_allowlist') ?? null;
  const hmacMethodId = rows.find((row) => row.method === 'hmac')?.id ?? null;

  const secrets = useQuery({
    queryKey: ['source-secrets', sourceId],
    queryFn: () => sourceSecrets(sourceId),
    enabled: hmacMethodId !== null,
  });

  const clear = () => setStaged(null);
  const save = useBindSender(sourceId, hmacMethodId, clear);
  const remove = useRemoveCheck(sourceId, clear);
  const requireAddresses = useRequireAddresses(sourceId, ipFilter);

  return {
    methods: rows,
    method,
    methodId: credentialed?.id ?? null,
    hmacMethodId,
    checked: rows.length > 0,
    preset,
    senderName: preset?.name ?? spec?.name ?? null,
    secretPrefix: spec ? secretPrefixOf(spec) : null,
    spec,
    rangeKeys: [...new Set(rangeKeys)],
    filteringKeys,
    enabledMethods: rows.filter((row) => row.enabled),
    requireAddresses,
    hasSecret: (secrets.data ?? []).some(
      (secret) =>
        secret.authMethodId === hmacMethodId && !secret.customerId && secret.state === 'active',
    ),
    addressesOnly: rows.length === 1 && rows[0]?.method === 'ip_allowlist',
    // `isLoading`, not `isPending`: a disabled query stays pending forever and would never leave the skeleton.
    isPending: methods.isPending || presets.isLoading || pinned.isLoading,
    isError: methods.isError || presets.isError || pinned.isError,
    error: methods.error ?? presets.error ?? pinned.error,
    refetch: () => void methods.refetch(),
    staged,
    stage: setStaged,
    picking,
    setPicking,
    save,
    remove,
  };
};
